import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Gavel } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VerifiedBadge from "@/components/VerifiedBadge";
import ReviewReportDialog from "@/components/ReviewReportDialog";
import BanUserDialog from "@/components/BanUserDialog";

function StarRow({ value, size = 16, onSelect, editable = false, testIdPrefix = "star" }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        const cls = filled ? "fill-yellow-400 text-yellow-400" : "text-zinc-600";
        return (
          <button
            key={n}
            type="button"
            disabled={!editable}
            onClick={() => editable && onSelect?.(n)}
            data-testid={`${testIdPrefix}-${n}`}
            className={`${editable ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"} disabled:cursor-default`}
            style={{ lineHeight: 0 }}
          >
            <Star style={{ width: size, height: size }} className={cls} />
          </button>
        );
      })}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export default function ReviewsSection({ server, onServerUpdate }) {
  const { user, loginWithDiscord } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [myReview, setMyReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [banTarget, setBanTarget] = useState(null); // { user_id, name } | null

  const isOwner = user && server?.owner_id === user.user_id;
  const isStaff = user?.role === "admin" || user?.role === "owner";

  const load = async () => {
    if (!server?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/servers/${server.id}/reviews`, {
        params: { limit: 50 },
      });
      setReviews(data.items || []);
      setTotal(data.total || 0);
      setMyReview(data.my_review || null);
      if (data.my_review) {
        setRating(data.my_review.rating);
        setComment(data.my_review.comment || "");
      }
    } catch {
      // silent — reviews just won't show
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [server?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!user) return loginWithDiscord();
    if (rating < 1 || rating > 5) {
      toast.error("Pick a star rating");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/servers/${server.id}/reviews`, {
        rating,
        comment: comment.trim(),
      });
      toast.success(myReview ? "Review updated" : "Review posted");
      setMyReview(data);
      // Refresh list to reflect update / new review
      await load();
      // Ask parent to refresh server object (avg_rating / review_count changed)
      onServerUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save review");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!myReview) return;
    if (!confirm("Delete your review?")) return;
    try {
      await api.delete(`/servers/${server.id}/reviews/${myReview.id}`);
      toast.success("Review deleted");
      setMyReview(null);
      setRating(0);
      setComment("");
      await load();
      onServerUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const removeAsAdmin = async (r) => {
    if (!confirm(`Delete ${r.user_name}'s review?`)) return;
    try {
      await api.delete(`/servers/${server.id}/reviews/${r.id}`);
      toast.success("Review deleted");
      await load();
      onServerUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const avg = Number(server?.avg_rating || 0);

  return (
    <section className="mt-12" data-testid="reviews-section">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan mb-2">
            Community
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-black">
            Ratings & Reviews
          </h2>
          <div className="mt-2 flex items-center gap-3 text-sm text-zinc-400">
            {total > 0 ? (
              <>
                <StarRow value={Math.round(avg)} size={16} />
                <span className="font-mono">
                  <span className="text-white font-bold">{avg.toFixed(1)}</span> · {total} review{total === 1 ? "" : "s"}
                </span>
              </>
            ) : (
              <span>No reviews yet — be the first to review this server.</span>
            )}
          </div>
        </div>
      </div>

      {/* Write / Edit review */}
      {!isOwner && (
        <div className="rounded-2xl border border-white/5 bg-[#0A0A0F] p-5 sm:p-6 mb-8" data-testid="review-form">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-bold">
                {myReview ? "Your review" : "Leave a review"}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {user
                  ? myReview
                    ? "Edit your rating anytime — only one per server."
                    : "One review per server. You can edit or delete it later."
                  : "Sign in with Discord to leave a review."}
              </div>
            </div>
            {user && (
              <StarRow
                value={rating}
                editable
                size={22}
                onSelect={setRating}
                testIdPrefix="review-star"
              />
            )}
          </div>

          {user ? (
            <>
              <textarea
                data-testid="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 1000))}
                placeholder="Tell others what makes this server great (optional)..."
                rows={3}
                className="mt-4 w-full bg-[#12121A] border border-white/10 rounded-lg px-3 py-2 text-sm placeholder-zinc-500 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple resize-none"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10px] font-mono text-zinc-600">
                  {comment.length}/1000
                </span>
                <div className="flex items-center gap-2">
                  {myReview && (
                    <button
                      onClick={remove}
                      data-testid="review-delete-btn"
                      className="h-9 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 text-xs font-semibold inline-flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                  <button
                    onClick={submit}
                    disabled={submitting || rating < 1}
                    data-testid="review-submit-btn"
                    className="h-9 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-bold transition-all disabled:opacity-50"
                  >
                    {submitting ? "Saving..." : myReview ? "Update review" : "Post review"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <button
              onClick={loginWithDiscord}
              data-testid="review-signin-btn"
              className="mt-4 h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-bold"
            >
              Sign in with Discord to review
            </button>
          )}
        </div>
      )}

      {isOwner && (
        <div className="rounded-2xl border border-white/5 bg-[#0A0A0F]/60 p-4 mb-8 text-sm text-zinc-500" data-testid="review-owner-notice">
          You can&apos;t review your own server — this is what other members will see.
        </div>
      )}

      {/* Review list */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl text-sm text-zinc-500" data-testid="reviews-empty">
          No reviews yet.
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div
              key={r.id}
              data-testid="review-item"
              className="rounded-xl border border-white/5 bg-[#0A0A0F] p-4 sm:p-5"
            >
              <div className="flex items-start gap-3">
                <img
                  src={r.user_avatar || "https://cdn.discordapp.com/embed/avatars/0.png"}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover bg-[#12121A]"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white truncate">{r.user_name}</span>
                    <VerifiedBadge verified={r.user_is_verified} size={13} label={false} />
                    <StarRow value={r.rating} size={13} />
                    <span className="text-[10px] font-mono text-zinc-500">
                      {timeAgo(r.updated_at || r.created_at)}
                      {r.updated_at && r.created_at && r.updated_at !== r.created_at ? " (edited)" : ""}
                    </span>
                  </div>
                  {r.comment ? (
                    <p className="mt-2 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">
                      {r.comment}
                    </p>
                  ) : null}
                </div>
                {(user?.role === "admin" || user?.role === "owner") && r.user_id !== user.user_id && (
                  <button
                    onClick={() => removeAsAdmin(r)}
                    title="Delete review (admin)"
                    data-testid={`admin-delete-review-${r.id}`}
                    className="h-8 w-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 flex items-center justify-center shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {isStaff && r.user_id !== user.user_id && (
                  <button
                    onClick={() =>
                      setBanTarget({ user_id: r.user_id, name: r.user_name })
                    }
                    title="Ban this user & wipe their reviews"
                    data-testid={`ban-reviewer-${r.id}`}
                    className="h-8 w-8 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 text-purple-300 flex items-center justify-center shrink-0"
                  >
                    <Gavel className="w-3.5 h-3.5" />
                  </button>
                )}
                {user && r.user_id !== user.user_id && (
                  <ReviewReportDialog review={r} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <BanUserDialog
        open={!!banTarget}
        onOpenChange={(v) => !v && setBanTarget(null)}
        target={banTarget}
        onBanned={() => {
          load();
          onServerUpdate?.();
        }}
      />
    </section>
  );
}
